import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { hasPermission } from '@/types';
import { useSearchParams } from 'react-router-dom';
import { mapy, studies, intakes as ujeciaApi, localGeomLayers, egib, geo, studyTypeRequiredLayers,
         type MapPhaseUpdate, type LocalGeomLayerDto, type Locality } from '@/api/endpoints';
import type { StudySummary, Intake, MapLayer } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { MapLibreMap, type Bbox4326, type MarkerMode, type ParcelsOverlay,
         type StackLayer, PSH_TYPES, PSH_MIN_ZOOM, type PshState } from '@/components/MapLibreMap';
import { useAuth } from '@/auth/AuthContext';

/** Jeden wpis stosu warstw kompozycji: kod + widoczność + krycie (0..100). Kolejność GÓRA→DÓŁ. */
interface StackEntry { code: string; visible: boolean; opacity: number; }

// Domyślna kolejność kategorii (GÓRA→DÓŁ): dane własne i ochronne na wierzchu, podkład na spodzie.
const CATEGORY_Z = ['Własne', 'Ochrona przyrody', 'Wody', 'Hydrogeologia', 'Podkład topograficzny'];
const zOf = (c: string) => { const i = CATEGORY_Z.indexOf(c); return i < 0 ? 50 : i; };

// MapLayer nie ma koloru — próbkę legendy wyprowadzamy z kategorii (jeden język kolorów w całym widoku).
const SWATCH: Record<string, CSSProperties> = {
  'Podkład topograficzny': { background: '#f5e6d3' },
  'Hydrogeologia':         { background: 'rgba(6,182,212,0.35)' },
  'Wody':                  { background: '#06b6d4' },
  'Ochrona przyrody':      { background: 'repeating-linear-gradient(45deg,#10a97a 0 3px,transparent 3px 6px), rgba(16,169,122,0.18)' },
  'Własne':                { background: '#7c5cff' },
};
const PALETTE = ['#06b6d4', '#7c5cff', '#10a97a', '#d48410', '#b97841', '#d8354b'];
const swatchFor = (cat: string): CSSProperties =>
  SWATCH[cat] ?? { background: PALETTE[[...cat].reduce((a, c) => a + c.charCodeAt(0), 0) % PALETTE.length] };

/** Ikona „ukryte" — brak w zestawie Icon; oko z ukośnikiem. */
function EyeOff({ className }: { className?: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" opacity=".4"/>
      <circle cx="12" cy="12" r="3" opacity=".4"/><path d="M3 3l18 18"/>
    </svg>
  );
}

/** Kółko postępu — jeden throbber dla całego widoku (wskaźniki ładowania warstw, danych i eksportu). */
function Spinner({ className = 'w-3 h-3' }: { className?: string }) {
  return (
    <svg className={'animate-spin ' + className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25"/>
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  );
}

export function Maps() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const deepLinkApplied = useRef(false);
  // Warstwy lokalne PostGIS: endpoint wymaga ADMIN_CONFIG — panel musi pytać o to samo.
  const isAdmin = hasPermission(user, 'ADMIN_CONFIG');
  const [layers, setLayers] = useState<MapLayer[]>([]);
  const [availability, setDostepnosc] = useState<Record<string, boolean>>({});
  const [stack, setStack] = useState<StackEntry[]>([]);
  const [loadingCodes, setLoadingCodes] = useState<Set<string>>(new Set()); // warstwy doczytujące kafelki (live)
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set());
  const [oprList, setOprList] = useState<StudySummary[]>([]);
  const [studyId, setStudyId] = useState<number | ''>('');
  const [wybraneIntake, setWybraneIntake] = useState<Intake | null>(null);
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [intakeFetchError, setIntakeFetchError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [drawnBy, setDrawnBy] = useState(user?.fullName ?? '');
  const [drawingNumber, setDrawingNumber] = useState('1');
  const [format, setFormat] = useState<'A2' | 'A3' | 'A4'>('A3');
  const [skala, setSkala] = useState('1:10 000');
  const [fileType, setFileType] = useState<'PNG' | 'PDF'>('PDF');
  const [drawMode, setDrawMode] = useState(false);
  const [markerMode, setMarkerMode] = useState<MarkerMode>('intake');
  const [centerTrigger, setCenterTrigger] = useState(0);
  const [parcelsEnabled, setParcelsEnabled] = useState(false);
  const [parcelsOverlay, setParcelsOverlay] = useState<ParcelsOverlay | null>(null);
  const [pshEnabled, setPshEnabled] = useState(false);
  const [pshHiddenTypes, setPshHiddenTypes] = useState<Set<string>>(new Set()); // typy PSH ukryte (odznaczone)
  const [pshStats, setPshStats] = useState<Record<string, number>>({});         // liczność typów PSH w widoku
  const [pshState, setPshState] = useState<PshState | null>(null);              // stan wczytywania warstwy (uwaga 4)
  const [parcelsLoading, setParcelsLoading] = useState(false);
  const [localLayers, setLocalLayers] = useState<LocalGeomLayerDto[] | null>(null);
  const [localOpen, setLocalOpen] = useState(false);
  const [refreshingLocal, setRefreshingLocal] = useState<string | null>(null);
  const [bbox, setBbox] = useState<Bbox4326 | null>(null);
  const [cursor, setCursor] = useState<{ lng: number; lat: number } | null>(null);
  // Wyszukiwarka miejscowości (GUGiK) — podpowiedzi z gminą + przelot mapy
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Locality[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTarget, setSearchTarget] = useState<{ lng: number; lat: number; label: string } | null>(null);
  const suppressSearchRef = useRef(false); // pomija ponowne zapytanie po wyborze podpowiedzi
  const [dragCode, setDragCode] = useState<string | null>(null);
  const [overCode, setOverCode] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [phases, setPhases] = useState<MapPhaseUpdate[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Pływające panele nad mapą: ukrywanie (open) i zwijanie do belki (collapsed) ──
  // Na wąskich ekranach panele startują ukryte, żeby nie przykrywały całej mapy.
  const szerokiEkran = () => typeof window === 'undefined' || window.innerWidth >= 1024;
  const [layersOpen, setLayersOpen] = useState(szerokiEkran);
  const [layersCollapsed, setLayersCollapsed] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);     // sekcja katalogu w panelu warstw
  const [exportOpen, setExportOpen] = useState(szerokiEkran);
  const [exportCollapsed, setExportCollapsed] = useState(false);
  const [legendOpen, setLegendOpen] = useState(szerokiEkran);

  useEffect(() => {
    Promise.all([mapy.layers(), studies.list()])
      .then(([w, o]) => {
        setLayers(w);
        setOprList(o);
        // Zaseeduj stos z warstw domyślnych, posortowany wg z-priorytetu kategorii (wierzch → index 0).
        setStack(
          w.filter(x => x.enabledByDefault)
           .sort((a, b) => zOf(a.category) - zOf(b.category))
           .map(x => ({ code: x.code, visible: true, opacity: x.defaultOpacity ?? 80 })),
        );
      })
      .catch(e => setErr(String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    localGeomLayers.list().then(setLocalLayers).catch(() => setLocalLayers([]));
  }, [isAdmin]);

  // Pobierz działki EGIB w bbox. Strategia: jak user wybrał ujęcie z koordami,
  // bbox to ±0.005° wokół ujęcia (~500 m). Inaczej, jeśli narysował bbox, używamy go.
  useEffect(() => {
    if (!parcelsEnabled) {
      setParcelsOverlay(null);
      return;
    }
    let bx: [number, number, number, number] | null = null;
    if (bbox) {
      bx = bbox;
    } else if (wybraneIntake?.latitude != null && wybraneIntake?.longitude != null) {
      const lon = Number(wybraneIntake.longitude);
      const lat = Number(wybraneIntake.latitude);
      const d = 0.005; // ~500 m wokół
      bx = [lon - d, lat - d, lon + d, lat + d];
    }
    if (!bx) return;
    let cancelled = false;
    setParcelsLoading(true);
    egib.parcelsInBbox(bx)
      .then(fc => { if (!cancelled) setParcelsOverlay(fc as unknown as ParcelsOverlay); })
      .catch(e => { if (!cancelled) setErr(String(e)); })
      .finally(() => { if (!cancelled) setParcelsLoading(false); });
    return () => { cancelled = true; };
  }, [parcelsEnabled, wybraneIntake, bbox]);

  // Podpowiedzi miejscowości — debounce 350 ms, min. 3 znaki. PRNG dopasowuje prefiksowo.
  useEffect(() => {
    if (suppressSearchRef.current) { suppressSearchRef.current = false; return; } // po wyborze nie odpytuj
    const q = search.trim();
    if (q.length < 3) { setSearchResults([]); setSearchLoading(false); return; }
    setSearchLoading(true);
    let cancelled = false;
    const t = setTimeout(() => {
      geo.localities(q)
        .then(rs => { if (!cancelled) { setSearchResults(rs); setSearchOpen(true); } })
        .catch(() => { if (!cancelled) setSearchResults([]); })
        .finally(() => { if (!cancelled) setSearchLoading(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search]);

  const pickLocality = (r: Locality) => {
    suppressSearchRef.current = true;
    setSearch(r.city);
    setSearchOpen(false);
    setSearchResults([]);
    setSearchLoading(false);
    setSearchTarget({ lng: r.lng, lat: r.lat, label: `${r.city} · gm. ${r.gmina}` });
  };

  // Wybór opracowania: pobieramy ujęcie, ze stanem ładowania + błędu (zamiast cichej catch).
  const onPickStudy = (v: number | '') => {
    setStudyId(v);
    setIntakeFetchError(null);
    setWybraneIntake(null);
    if (v === '') return;
    const op = oprList.find(x => x.id === v);
    if (!op) {
      setIntakeFetchError(`Opracowanie #${v} nie znalezione na liście.`);
      return;
    }
    // Auto-włącz warstwy WMS/WMTS wymagane dla typu tego opracowania (słownik „Warstwy per typ").
    studyTypeRequiredLayers.list(op.type)
      .then(reqs => applyRequiredLayers(reqs.map(r => r.layerCode)))
      .catch(() => { /* brak konfiguracji warstw dla typu — pomijamy cicho */ });
    if (!op.intakeId) {
      setIntakeFetchError(`Opracowanie ${op.number} nie ma przypisanego ujęcia.`);
      return;
    }
    setIntakeLoading(true);
    ujeciaApi.get(op.intakeId)
      .then(intake => {
        setWybraneIntake(intake);
        if (intake.latitude == null || intake.longitude == null) {
          setIntakeFetchError(`Ujęcie „${intake.name}" nie ma współrzędnych — marker nie pojawi się na mapie. Uzupełnij lat/lng w karcie ujęcia.`);
        }
      })
      .catch(e => {
        const msg = e instanceof Error ? e.message : String(e);
        setIntakeFetchError(`Nie udało się pobrać ujęcia (id=${op.intakeId}): ${msg}`);
      })
      .finally(() => setIntakeLoading(false));
  };

  const onRefreshLocal = async (code: string) => {
    setRefreshingLocal(code);
    setErr(null);
    try {
      const updated = await localGeomLayers.refresh(code);
      setLocalLayers(prev => (prev ?? []).map(l => l.code === code ? updated : l));
      const newLayers = await mapy.layers();
      setLayers(newLayers);
      setInfo(`Pobrano ${updated.featureCount} obiektów dla ${updated.displayName}.`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshingLocal(null);
    }
  };

  // ── Model danych: mapa kodów, grupowanie katalogu, stos rozwiązany do obiektów ──
  const byCode = useMemo(() => Object.fromEntries(layers.map(l => [l.code, l] as const)), [layers]);
  const grouped = useMemo(() => {
    const g: Record<string, MapLayer[]> = {};
    for (const w of layers) (g[w.category] = g[w.category] ?? []).push(w);
    return g;
  }, [layers]);
  const inStack = useMemo(() => new Set(stack.map(s => s.code)), [stack]);
  // GÓRA→DÓŁ dla mapy i legendy; odrzuć osierocone kody (np. warstwa usunięta po stronie backendu).
  const renderStack = useMemo<StackLayer[]>(
    () => stack
      .map(s => ({ layer: byCode[s.code], opacity: s.opacity, visible: s.visible }))
      .filter((s): s is StackLayer => !!s.layer),
    [stack, byCode],
  );
  const visibleCount = useMemo(() => renderStack.filter(s => s.visible).length, [renderStack]);

  // ── Wskaźniki ładowania danych mapy (throbbery) ──
  // Kafelki WMS/WMTS liczy sama mapa (onLayerLoadingChange); dokładamy warstwy pobierane
  // po HTTP poza silnikiem MapLibre: działki EGIB i otwory PSH.
  const pshLoading = pshEnabled && pshState?.status === 'loading';
  const loadingItems = useMemo(() => {
    const items = [...loadingCodes].map(c => byCode[c]?.name ?? c);
    if (parcelsLoading) items.push('Działki EGIB');
    if (pshLoading) items.push('Otwory PSH (CBDG)');
    return items;
  }, [loadingCodes, byCode, parcelsLoading, pshLoading]);
  // Mapa emituje zbiór przy KAŻDYM kafelku — bez porównania zawartości każdy kafel przerysowywałby widok.
  const onLayerLoading = useCallback((next: Set<string>) => {
    setLoadingCodes(prev => (prev.size === next.size && [...next].every(c => prev.has(c))) ? prev : next);
  }, []);

  // ── Mutatory stosu ──
  const addToStack = (code: string) => setStack(s =>
    s.some(x => x.code === code) ? s
      : [{ code, visible: true, opacity: byCode[code]?.defaultOpacity ?? 80 }, ...s]); // nowa → wierzch (QGIS)
  const removeFromStack = (code: string) => setStack(s => s.filter(x => x.code !== code));
  const toggleVisible = (code: string) => setStack(s => s.map(x => x.code === code ? { ...x, visible: !x.visible } : x));
  const setOpacity = (code: string, op: number) => setStack(s => s.map(x => x.code === code ? { ...x, opacity: op } : x));
  const addGroup = (list: MapLayer[]) => setStack(s => {
    const have = new Set(s.map(x => x.code));
    const add = list.filter(l => !have.has(l.code)).map(l => ({ code: l.code, visible: true, opacity: l.defaultOpacity ?? 80 }));
    return [...add, ...s];
  });
  const removeGroup = (list: MapLayer[]) => { const c = new Set(list.map(l => l.code)); setStack(s => s.filter(x => !c.has(x.code))); };
  // Tri-state oko grupy: przełącza widoczność wpisów tej kategorii, które są w stosie.
  const toggleGroupVisible = (list: MapLayer[]) => setStack(s => {
    const codes = new Set(list.map(l => l.code));
    const members = s.filter(x => codes.has(x.code));
    const anyOn = members.some(x => x.visible);
    return s.map(x => codes.has(x.code) ? { ...x, visible: !anyOn } : x);
  });
  // Reorder (globalny, natywny drag). Wstawiamy przeciąganą warstwę PRZED docelową — spójnie
  // w obu kierunkach (usunięcie źródła najpierw, potem indeks celu liczony na skróconej liście).
  const reorder = (from: string | null, to: string) => {
    if (!from || from === to) return;
    setStack(s => {
      const moved = s.find(x => x.code === from);
      if (!moved) return s;
      const rest = s.filter(x => x.code !== from);
      const ti = rest.findIndex(x => x.code === to);
      if (ti < 0) return s;
      rest.splice(ti, 0, moved);
      return rest;
    });
  };

  // Auto-włączenie warstw wymaganych dla wybranego typu opracowania (union z bieżącym stosem).
  // Nowe wpisy sortujemy między sobą wg z-priorytetu kategorii i doklejamy na wierzch (jak addGroup) —
  // NIE przesortowujemy całego stosu, żeby nie skasować ręcznej kolejności ustawionej przez usera.
  const applyRequiredLayers = (codes: string[]) => {
    if (codes.length === 0) return;
    setStack(prev => {
      const have = new Set(prev.map(s => s.code));
      const toAdd = codes
        .filter(c => byCode[c] && !have.has(c))
        .map(c => ({ code: c, visible: true, opacity: byCode[c].defaultOpacity ?? 80 }))
        .sort((a, b) => zOf(byCode[a.code]?.category ?? '') - zOf(byCode[b.code]?.category ?? ''));
      if (toAdd.length === 0) return prev;
      return [...toAdd, ...prev];
    });
  };

  // Deep-link z karty opracowania: /mapy?studyId=X → wybór opracowania + auto-włączenie warstw.
  useEffect(() => {
    if (deepLinkApplied.current || loading) return;
    const sid = searchParams.get('studyId');
    if (!sid) return;
    const id = Number(sid);
    if (!Number.isFinite(id) || !oprList.some(o => o.id === id)) return;
    deepLinkApplied.current = true;
    onPickStudy(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, oprList, searchParams]);

  const onSprawdz = () => {
    setBusy(true);
    setErr(null);
    mapy.availability().then(setDostepnosc).catch(e => setErr(String(e))).finally(() => setBusy(false));
  };

  // Postęp generowania żyje w panelu kompozycji — pokaż go, gdy eksport startuje z panelem ukrytym.
  useEffect(() => { if (busy) { setExportOpen(true); setExportCollapsed(false); } }, [busy]);

  const intakeMaCoords = !!(wybraneIntake?.latitude && wybraneIntake?.longitude);
  const canFetch = !!studyId && visibleCount > 0 && (!!bbox || intakeMaCoords);

  const onFetch = async () => {
    if (!studyId) { setErr('Wybierz opracowanie'); return; }
    if (visibleCount === 0) { setErr('Włącz widoczność co najmniej jednej warstwy'); return; }
    if (!bbox && !intakeMaCoords) {
      setErr('Ujęcie nie ma współrzędnych — uzupełnij latitude/longitude w karcie ujęcia lub narysuj prostokąt na mapie');
      return;
    }
    setBusy(true);
    setErr(null);
    setInfo(null);
    setPhases([]);
    try {
      // Wysyłamy widoczne warstwy w kolejności DÓŁ→GÓRA (backend komponuje w kolejności wejścia).
      const layerCodes = renderStack.filter(s => s.visible).map(s => s.layer.code).reverse();
      const result = await mapy.fetchWithProgress({
        studyId: Number(studyId),
        layerCodes,
        format,
        scale: skala,
        fileType,
        bbox: bbox ?? undefined,
        name: name || undefined,
        drawnBy: drawnBy || undefined,
        drawingNumber: drawingNumber || undefined,
      }, (p) => setPhases(prev => [...prev, p]));
      const kb = Math.round(result.sizeBytes / 1024);
      setInfo(`Pobrano: ${result.filename} (${kb} kB) · zapisane w ${result.storageUrl || 'MinIO mapy-cache'}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-ink-400">Ładowanie…</div>;

  const statusDot = (code: string) =>
    code in availability ? (availability[code] ? 'sdot-green' : 'sdot-red') : 'sdot-gray';

  return (
    <div className="max-w-[1600px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Mapy · GIS</h1>
          <div className="text-ink-400 text-[13px]">Kompozycja warstw WMS (GUGiK · PIG · Wody Polskie · GDOŚ) → podgląd na żywo → PNG/PDF</div>
        </div>
        <button disabled={busy} onClick={onSprawdz} className="btn btn-secondary btn-sm">
          <Icon name="check" className="w-3.5 h-3.5"/> Sprawdź dostępność
        </button>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}
      {info && <div className="text-green-600 mb-3 text-[12px]">{info}</div>}

      {/* ── HERO: mapa na całą szerokość; panele nakładane, zwijane i ukrywalne ── */}
      <div className="card flex flex-col h-[calc(100vh-190px)] min-h-[560px]">
        <div className="card-head !py-2 !px-3 flex-wrap">
          <div className="card-title">Podgląd kompozycji</div>
          <span className="text-[11px] text-ink-400 hidden sm:inline">{visibleCount} widocznych · {stack.length} w stosie</span>
          {loadingItems.length > 0 && (
            <span
              className="inline-flex items-center gap-1.5 text-[11px] text-water-700 bg-water-100 px-2 py-0.5 rounded-full"
              title={'Wczytywanie: ' + loadingItems.join(', ')}
            >
              <Spinner className="w-3 h-3"/> wczytywanie ({loadingItems.length})
            </span>
          )}
          <div className="ml-auto flex items-center gap-1">
            <button
              className={'btn btn-sm ' + (layersOpen ? 'btn-secondary' : 'btn-ghost')}
              title={layersOpen ? 'Ukryj panel warstw' : 'Pokaż panel warstw'}
              onClick={() => setLayersOpen(o => !o)}
            ><Icon name="layers" className="w-3.5 h-3.5"/> Warstwy</button>
            <button
              className={'btn btn-sm ' + (legendOpen ? 'btn-secondary' : 'btn-ghost')}
              title={legendOpen ? 'Ukryj legendę' : 'Pokaż legendę'}
              onClick={() => setLegendOpen(o => !o)}
            ><Icon name="map" className="w-3.5 h-3.5"/> Legenda</button>
            <button
              className={'btn btn-sm ' + (exportOpen ? 'btn-secondary' : 'btn-ghost')}
              title={exportOpen ? 'Ukryj panel kompozycji' : 'Pokaż panel kompozycji'}
              onClick={() => setExportOpen(o => !o)}
            ><Icon name="download" className="w-3.5 h-3.5"/> Kompozycja</button>
          </div>
        </div>
        <div className="card-body !p-0 flex-1 min-h-0 relative overflow-hidden">
          <MapLibreMap
            layerStack={renderStack}
            intake={wybraneIntake}
            height="100%"
            drawMode={drawMode}
            bbox={bbox}
            onBboxChange={(b) => { setBbox(b); setDrawMode(false); }}
            markerMode={markerMode}
            centerTrigger={centerTrigger}
            showPsh={pshEnabled}
            pshHiddenTypes={[...pshHiddenTypes]}
            onPshStats={setPshStats}
            onPshState={setPshState}
            parcelsOverlay={parcelsOverlay}
            onCursorMove={setCursor}
            searchTarget={searchTarget}
            onLayerLoadingChange={onLayerLoading}
          />

          {/* ── LEWA KOLUMNA (nakładka): wyszukiwarka · narzędzia · warstwy · legenda ──
              Kolumna flex zamiast kilku niezależnych „absolute" — panele nigdy na siebie nie wchodzą,
              a wolne miejsce między nimi przepuszcza mysz do mapy (pointer-events-none na kontenerze). */}
          <div className="absolute top-3 left-3 bottom-3 z-20 w-[min(86vw,330px)] flex flex-col gap-2 pointer-events-none">

            {/* wyszukiwarka miejscowości (GUGiK) — podpowiedzi z gminą */}
            <div className="relative shrink-0 pointer-events-auto">
              <Icon name="search" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none"/>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                onFocus={() => { if (searchResults.length) setSearchOpen(true); }}
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                onKeyDown={e => {
                  if (e.key === 'Escape') setSearchOpen(false);
                  else if (e.key === 'Enter' && searchResults.length) { e.preventDefault(); pickLocality(searchResults[0]); }
                }}
                placeholder="Szukaj miejscowości…"
                className="w-full pl-8 pr-8 py-2 rounded-lg border border-ink-200 bg-white shadow-md text-[13px] outline-none focus:border-water-500 focus:ring-2 focus:ring-water-500/20"
              />
              {searchLoading ? (
                <Spinner className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-water-500"/>
              ) : search ? (
                <button
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => { setSearch(''); setSearchResults([]); setSearchTarget(null); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-300 hover:text-ink-600"
                  title="Wyczyść"
                ><Icon name="x" className="w-4 h-4"/></button>
              ) : null}
              {searchOpen && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border border-ink-200 rounded-lg shadow-lg overflow-hidden max-h-[240px] overflow-y-auto">
                  {searchResults.map((r, i) => (
                    <button
                      key={r.simc ?? `${r.city}-${i}`}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => pickLocality(r)}
                      className="w-full text-left px-3 py-1.5 hover:bg-water-50/60 border-b border-ink-50 last:border-0"
                    >
                      <div className="text-[13px] font-semibold text-ink-800">{r.city}</div>
                      <div className="text-[11px] text-ink-500">
                        {r.type ? `${r.type} · ` : ''}gm. {r.gmina}{r.county ? ` · pow. ${r.county}` : ''}{r.voivodeship ? ` · ${r.voivodeship}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {searchOpen && !searchLoading && searchResults.length === 0 && search.trim().length >= 3 && (
                <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border border-ink-200 rounded-lg shadow-lg px-3 py-2 text-[12px] text-ink-500">
                  Brak miejscowości pasujących do „{search.trim()}".
                </div>
              )}
            </div>

            {/* pasek narzędzi mapy */}
            <div className="shrink-0 self-start pointer-events-auto flex gap-1 bg-white/95 backdrop-blur-sm border border-ink-200 rounded-lg shadow-md p-1">
              <button
                className={'btn btn-icon !w-9 !h-9 ' + (drawMode ? 'btn-primary' : 'btn-ghost')}
                title={drawMode ? 'Rysowanie aktywne — kliknij i przeciągnij po mapie' : 'Narysuj obszar eksportu'}
                onClick={() => setDrawMode(d => !d)}
              ><Icon name="filter" className="w-4 h-4"/></button>
              <button
                className="btn btn-icon !w-9 !h-9 btn-ghost"
                disabled={!intakeMaCoords}
                title={intakeMaCoords ? 'Centruj na ujęciu' : 'Wybierz opracowanie z ujęciem zawierającym współrzędne'}
                onClick={() => setCenterTrigger(t => t + 1)}
              ><Icon name="droplet" className="w-4 h-4"/></button>
              <button
                className={'btn btn-icon !w-9 !h-9 relative ' + (parcelsEnabled ? 'btn-primary' : 'btn-ghost')}
                disabled={!intakeMaCoords && !bbox}
                title={parcelsLoading ? 'Pobieranie działek…' : parcelsEnabled ? `Działki EGIB: ${parcelsOverlay?.features.length ?? 0} (kliknij, aby wyłączyć)` : 'Pokaż klikalne granice działek EGIB'}
                onClick={() => setParcelsEnabled(e => !e)}
              >
                <Icon name="layout" className="w-4 h-4"/>
                {parcelsLoading && <Spinner className="absolute -top-0.5 -right-0.5 w-3 h-3 text-water-600"/>}
              </button>
              <button
                className={'btn btn-icon !w-9 !h-9 relative ' + (pshEnabled ? 'btn-primary' : 'btn-ghost')}
                title="Otwory wiertnicze PSH (CBDG PIG-PIB) — widoczne od zoom ≥ 10, klik → karta"
                onClick={() => setPshEnabled(e => !e)}
              >
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#b45309', border: '1.5px solid #fff', boxShadow: '0 0 0 1px #b45309' }}/>
                {pshLoading && <Spinner className="absolute -top-0.5 -right-0.5 w-3 h-3 text-water-600"/>}
              </button>
              {bbox && (
                <button
                  className="btn btn-icon !w-9 !h-9 btn-ghost"
                  title="Wyczyść narysowany obszar"
                  onClick={() => { setBbox(null); setDrawMode(false); }}
                ><Icon name="x" className="w-4 h-4"/></button>
              )}
            </div>

            {/* ── PANEL WARSTW: kompozycja (stos) + katalog w jednym ── */}
            {layersOpen && (
              <section className={'pointer-events-auto flex flex-col min-h-0 bg-white/95 backdrop-blur-sm border border-ink-200 rounded-lg shadow-lg '
                + (layersCollapsed ? 'shrink-0' : 'flex-1')}>
                <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-ink-100 shrink-0">
                  <Icon name="layers" className="w-4 h-4 text-ink-500 shrink-0"/>
                  <div className="card-title">Warstwy</div>
                  <span className="pill pill-water !px-1.5">{visibleCount}/{stack.length}</span>
                  {loadingCodes.size > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-water-600" title="Warstwy doczytujące kafelki">
                      <Spinner className="w-3 h-3"/>{loadingCodes.size}
                    </span>
                  )}
                  <button
                    className="ml-auto btn btn-icon !w-6 !h-6 btn-ghost shrink-0"
                    title={layersCollapsed ? 'Rozwiń panel' : 'Zwiń panel'}
                    onClick={() => setLayersCollapsed(c => !c)}
                  ><Icon name="chevron-down" className={'w-4 h-4 transition ' + (layersCollapsed ? '-rotate-90' : '')}/></button>
                  <button
                    className="btn btn-icon !w-6 !h-6 btn-ghost shrink-0"
                    title="Ukryj panel (wróć przyciskiem „Warstwy” nad mapą)"
                    onClick={() => setLayersOpen(false)}
                  ><Icon name="x" className="w-3.5 h-3.5"/></button>
                </div>

                {!layersCollapsed && (
                  <div className="overflow-auto flex-1 min-h-0 p-1.5">

                    {/* A · stos kompozycji (kolejność rysowania: góra = wierzch) */}
                    <div className="flex items-center gap-1.5 px-1 pb-1 mb-1 border-b border-ink-100">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">W kompozycji</span>
                      <span className="text-[10px] text-ink-400">({stack.length})</span>
                      <button
                        className="btn btn-ghost btn-xs ml-auto"
                        title={visibleCount ? 'Ukryj wszystkie' : 'Pokaż wszystkie'}
                        disabled={stack.length === 0}
                        onClick={() => setStack(s => s.map(x => ({ ...x, visible: visibleCount === 0 })))}
                      >
                        <Icon name="eye" className="w-3.5 h-3.5"/> {visibleCount}/{stack.length}
                      </button>
                    </div>

                    {stack.length === 0 ? (
                      <div className="text-center text-ink-400 text-[12px] py-5 px-3">
                        Brak warstw w kompozycji.
                        <button className="btn btn-secondary btn-sm mt-2 mx-auto" onClick={() => setCatalogOpen(true)}>
                          <Icon name="plus" className="w-3.5 h-3.5"/> Wybierz z katalogu
                        </button>
                      </div>
                    ) : renderStack.map(({ layer: w, visible, opacity }) => {
                      const isOver = overCode === w.code && dragCode !== w.code;
                      return (
                        <div
                          key={w.code}
                          draggable
                          onDragStart={e => { setDragCode(w.code); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', w.code); }}
                          onDragOver={e => { e.preventDefault(); if (overCode !== w.code) setOverCode(w.code); }}
                          onDrop={e => { e.preventDefault(); reorder(dragCode, w.code); setDragCode(null); setOverCode(null); }}
                          onDragEnd={() => { setDragCode(null); setOverCode(null); }}
                          className={'group flex items-center gap-2 px-1.5 py-2 rounded-md border '
                            + (isOver ? 'border-water-500 bg-water-50/60 ' : 'border-transparent hover:bg-ink-50 ')
                            + (dragCode === w.code ? 'opacity-40 ' : '')
                            + (visible ? '' : 'opacity-60')}
                        >
                          <span className="text-ink-300 cursor-grab active:cursor-grabbing shrink-0" title="Przeciągnij, aby zmienić kolejność rysowania (góra = wierzch)">
                            <Icon name="more" className="w-3.5 h-3.5 rotate-90"/>
                          </span>
                          <button
                            onClick={() => toggleVisible(w.code)}
                            title={visible ? 'Ukryj warstwę' : 'Pokaż warstwę'}
                            className={'shrink-0 ' + (visible ? 'text-ink-700 hover:text-water-600' : 'text-ink-300 hover:text-ink-500')}
                          >
                            {visible ? <Icon name="eye" className="w-4 h-4"/> : <EyeOff className="w-4 h-4"/>}
                          </button>
                          <span className="w-3.5 h-3.5 rounded-[3px] shrink-0 border border-black/10" style={swatchFor(w.category)}/>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              {loadingCodes.has(w.code)
                                ? <span className="shrink-0 w-3 h-3 rounded-full border-[1.5px] border-water-500 border-t-transparent animate-spin"
                                        title="Doczytywanie kafelków…"/>
                                : <span className={'sdot shrink-0 ' + statusDot(w.code)}/>}
                              <span className="flex-1 truncate text-[12px] text-ink-800" title={w.name}>{w.name}</span>
                              {loadingCodes.has(w.code) && <span className="text-[10px] text-water-600 shrink-0">ładowanie…</span>}
                            </div>
                            <input
                              type="range" min={0} max={100} value={opacity}
                              onChange={e => setOpacity(w.code, Number(e.target.value))}
                              disabled={!visible}
                              title="Krycie warstwy"
                              className="w-full h-1 mt-1.5 accent-water-500 cursor-pointer disabled:opacity-40"
                            />
                          </div>
                          <span className="font-mono text-[10px] text-ink-400 w-8 text-right tabular-nums shrink-0">{opacity}%</span>
                          <button
                            onClick={() => removeFromStack(w.code)}
                            title="Usuń z kompozycji"
                            className="text-ink-300 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 group-focus-within:opacity-100 transition"
                          >
                            <Icon name="x" className="w-3.5 h-3.5"/>
                          </button>
                        </div>
                      );
                    })}

                    {/* B · katalog — ta sama lista, sekcja rozwijana (jeden panel zamiast dwóch kart) */}
                    <button
                      className="sticky top-0 z-[3] w-full mt-2 flex items-center gap-1.5 px-1 py-1.5 bg-white border-y border-ink-100 text-left hover:bg-ink-50"
                      onClick={() => setCatalogOpen(o => !o)}
                      title={catalogOpen ? 'Zwiń katalog' : 'Rozwiń katalog — dodaj warstwy do kompozycji'}
                    >
                      <Icon name={catalogOpen ? 'chevron-down' : 'chevron'} className="w-3.5 h-3.5 text-ink-400"/>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500">Katalog warstw</span>
                      <span className="ml-auto text-[10px] text-ink-400">{stack.length}/{layers.length} w kompozycji</span>
                    </button>

                    {catalogOpen && Object.entries(grouped).map(([kat, list]) => {
                      const isColl = collapsedCats.has(kat);
                      const added = list.filter(l => inStack.has(l.code)).length;
                      const allIn = added === list.length;
                      const memberVis = stack.filter(x => list.some(l => l.code === x.code) && x.visible).length;
                      const src = list[0]?.source ?? '';
                      return (
                        <div key={kat} className="mb-1">
                          <div className="sticky top-[30px] z-[2] bg-white flex items-center gap-1.5 px-1.5 py-1 rounded hover:bg-ink-50 border-b border-ink-100">
                            <button
                              className="text-ink-400"
                              title={isColl ? 'Rozwiń' : 'Zwiń'}
                              onClick={() => setCollapsedCats(c => { const n = new Set(c); n.has(kat) ? n.delete(kat) : n.add(kat); return n; })}
                            >
                              <Icon name={isColl ? 'chevron' : 'chevron-down'} className="w-3.5 h-3.5"/>
                            </button>
                            <span className="w-3 h-3 rounded-[2px] shrink-0 border border-black/10" style={swatchFor(kat)}/>
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 truncate">{kat}</div>
                              <div className="font-mono text-[9px] text-ink-400 truncate">{src}</div>
                            </div>
                            <span className="pill pill-gray text-[10px]">{added}/{list.length}</span>
                            <button
                              disabled={added === 0}
                              className={'shrink-0 disabled:opacity-30 ' + (memberVis === 0 ? 'text-ink-300' : memberVis === added ? 'text-water-600' : 'text-ink-400 opacity-70')}
                              title="Pokaż/ukryj warstwy grupy będące w kompozycji"
                              onClick={() => toggleGroupVisible(list)}
                            >
                              {memberVis === 0 ? <EyeOff className="w-3.5 h-3.5"/> : <Icon name="eye" className="w-3.5 h-3.5"/>}
                            </button>
                            <button
                              className="btn btn-ghost btn-sm !px-1"
                              title={allIn ? 'Usuń całą grupę z kompozycji' : 'Dodaj całą grupę do kompozycji'}
                              onClick={() => allIn ? removeGroup(list) : addGroup(list)}
                            >
                              <Icon name={allIn ? 'x' : 'plus'} className="w-3 h-3"/>
                            </button>
                          </div>
                          {!isColl && list.map(l => {
                            const on = inStack.has(l.code);
                            return (
                              <button
                                key={l.id}
                                onClick={() => on ? removeFromStack(l.code) : addToStack(l.code)}
                                className={'w-full flex items-center gap-2 pl-6 pr-2 py-1.5 rounded text-left text-[12px] '
                                  + (on ? 'bg-water-50/50 text-ink-800' : 'text-ink-600 hover:bg-ink-50')}
                              >
                                <span className={on ? 'text-water-600' : 'text-ink-300'}>
                                  <Icon name={on ? 'check' : 'plus'} className="w-3.5 h-3.5"/>
                                </span>
                                <span className="w-3 h-3 rounded-[2px] shrink-0 border border-black/10" style={swatchFor(l.category)}/>
                                <span className="flex-1 truncate" title={l.name}>{l.name}</span>
                                {loadingCodes.has(l.code)
                                  ? <span className="shrink-0 w-2.5 h-2.5 rounded-full border-[1.5px] border-water-500 border-t-transparent animate-spin" title="Doczytywanie kafelków…"/>
                                  : <span className={'sdot ' + statusDot(l.code)}/>}
                                <span className="font-mono text-[10px] text-ink-400">{l.protocol}</span>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {/* interaktywna legenda — druga powierzchnia sterowania (klik ✕ = ukryj warstwę) */}
            {legendOpen && (visibleCount > 0 || pshEnabled || parcelsOverlay || bbox) && (
              <div className="pointer-events-auto shrink-0 mt-auto max-h-[38%] overflow-auto w-[240px] bg-white/95 backdrop-blur-sm border border-ink-200 rounded-lg shadow-md px-3 py-2">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-400">Legenda · {visibleCount}</div>
                  <button
                    className="ml-auto text-ink-300 hover:text-ink-600"
                    title="Ukryj legendę (wróć przyciskiem „Legenda” nad mapą)"
                    onClick={() => setLegendOpen(false)}
                  ><Icon name="x" className="w-3 h-3"/></button>
                </div>
                {renderStack.filter(s => s.visible).map(s => (
                  <div key={s.layer.code} className="group flex items-center gap-2 mb-1 last:mb-0">
                    <span className="w-3 h-3 rounded-[2px] shrink-0 border border-black/10" style={{ ...swatchFor(s.layer.category), opacity: s.opacity / 100 }}/>
                    <span className="flex-1 text-[11px] text-ink-700 truncate" title={s.layer.name}>{s.layer.name}</span>
                    {loadingCodes.has(s.layer.code)
                      ? <span className="shrink-0 w-2.5 h-2.5 rounded-full border-[1.5px] border-water-500 border-t-transparent animate-spin" title="Doczytywanie kafelków…"/>
                      : <span className="font-mono text-[10px] text-ink-400">{s.opacity}%</span>}
                    <button onClick={() => toggleVisible(s.layer.code)} title="Ukryj" className="text-ink-300 hover:text-red-500 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 group-focus-within:opacity-100">
                      <Icon name="x" className="w-3 h-3"/>
                    </button>
                  </div>
                ))}
                {/* overlaye niebędące warstwami WMS */}
                {(pshEnabled || parcelsOverlay || parcelsLoading || bbox || (intakeMaCoords && markerMode === 'intake')) && (
                  <div className="mt-1.5 pt-1.5 border-t border-ink-100 flex flex-col gap-1">
                    {intakeMaCoords && markerMode === 'intake' && (
                      <div className="flex items-center gap-2 text-[11px] text-ink-600">
                        <span className="inline-block w-2.5 h-2.5 shrink-0" style={{ background: '#06B6D4', borderRadius: '50% 50% 50% 0', transform: 'rotate(45deg)' }}/> Ujęcie
                      </div>
                    )}
                    {parcelsLoading ? (
                      <div className="flex items-center gap-2 text-[11px] text-water-600">
                        <Spinner className="w-3 h-3 shrink-0"/> Działki EGIB — pobieranie…
                      </div>
                    ) : parcelsOverlay && (
                      <div className="flex items-center gap-2 text-[11px] text-ink-600">
                        <span className="inline-block w-3 h-3 shrink-0 rounded-[2px]" style={{ background: 'rgba(245,158,11,0.25)', border: '1px solid #92400e' }}/> Działki EGIB ({parcelsOverlay.features.length})
                      </div>
                    )}
                    {pshEnabled && (() => {
                      const present = PSH_TYPES.filter(t => (pshStats[t.key] ?? 0) > 0);
                      const total = Object.values(pshStats).reduce((a, b) => a + b, 0);
                      return (
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1.5 text-[11px] text-ink-600 font-semibold">
                            <span>Otwory PSH · typ</span>
                            <span className="text-ink-400 font-normal">({total})</span>
                            {pshLoading && <Spinner className="w-3 h-3 text-water-600"/>}
                          </div>
                          {present.length === 0 ? (
                            // uwaga 4: rozdzielone przypadki — dotąd awaria usługi wyglądała jak „brak otworów"
                            <div className={'text-[10px] pl-1 ' + (pshState?.status === 'error' ? 'text-red-500' : 'text-ink-400')}>
                              {pshState?.status === 'zoom' ? `Przybliż mapę (zoom ≥ ${PSH_MIN_ZOOM}), aby wczytać otwory.`
                                : pshState?.status === 'loading' ? 'Wczytywanie otworów z CBDG…'
                                : pshState?.status === 'error' ? 'Usługa CBDG nie odpowiada — spróbuj ponownie za chwilę.'
                                : 'Brak otworów w tym widoku.'}
                            </div>
                          ) : present.map(t => {
                            const on = !pshHiddenTypes.has(t.key);
                            return (
                              <label key={t.key} className="flex items-center gap-1.5 text-[11px] text-ink-700 cursor-pointer pl-1">
                                <input
                                  type="checkbox" checked={on}
                                  onChange={() => setPshHiddenTypes(prev => {
                                    const n = new Set(prev); if (on) n.add(t.key); else n.delete(t.key); return n;
                                  })}
                                  className="w-3 h-3 accent-water-500"
                                />
                                <span className="inline-block w-2.5 h-2.5 shrink-0 rounded-full" style={{ background: t.color, border: `1.5px solid #fff`, boxShadow: `0 0 0 1px ${t.color}` }}/>
                                <span className="flex-1 truncate" title={t.label}>{t.label}</span>
                                <span className="font-mono text-[10px] text-ink-400">{pshStats[t.key]}</span>
                              </label>
                            );
                          })}
                        </div>
                      );
                    })()}
                    {bbox && (
                      <div className="flex items-center gap-2 text-[11px] text-ink-600">
                        <span className="inline-block w-3 h-3 shrink-0 rounded-[2px]" style={{ background: 'rgba(216,53,75,0.12)', border: '1px dashed #d8354b' }}/> Obszar eksportu
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── PRAWA KOLUMNA (nakładka): kompozycja / eksport. right-14 zostawia rynnę
              na kontrolki MapLibre (zoom top-right, podziałka bottom-right). ── */}
          {exportOpen && (
            <div className="absolute top-3 right-14 bottom-3 z-20 w-[min(86vw,340px)] flex flex-col pointer-events-none">
              <section className={'pointer-events-auto flex flex-col min-h-0 bg-white/95 backdrop-blur-sm border border-ink-200 rounded-lg shadow-lg '
                + (exportCollapsed ? 'shrink-0' : 'flex-1')}>
                <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-ink-100 shrink-0">
                  <Icon name="download" className="w-4 h-4 text-ink-500 shrink-0"/>
                  <div className="card-title">Kompozycja</div>
                  {busy && <Spinner className="w-3 h-3 text-ai-600"/>}
                  <button
                    className="ml-auto btn btn-icon !w-6 !h-6 btn-ghost shrink-0"
                    title={exportCollapsed ? 'Rozwiń panel' : 'Zwiń panel'}
                    onClick={() => setExportCollapsed(c => !c)}
                  ><Icon name="chevron-down" className={'w-4 h-4 transition ' + (exportCollapsed ? '-rotate-90' : '')}/></button>
                  <button
                    className="btn btn-icon !w-6 !h-6 btn-ghost shrink-0"
                    title="Ukryj panel (wróć przyciskiem „Kompozycja” nad mapą)"
                    onClick={() => setExportOpen(false)}
                  ><Icon name="x" className="w-3.5 h-3.5"/></button>
                </div>
                {!exportCollapsed && (
                  <div className="p-3 flex flex-col gap-3 overflow-auto flex-1 min-h-0">
            <label className="field">
              <span className="field-label">
                Opracowanie
                {intakeLoading && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-water-700">
                    <Spinner className="w-3 h-3"/>
                    pobieram ujęcie…
                  </span>
                )}
              </span>
              <SearchableSelect
                value={studyId === '' ? '' : String(studyId)}
                onChange={v => onPickStudy(v ? Number(v) : '')}
              >
                <option value="">— wybierz —</option>
                {oprList.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.number} — {o.title}{!o.intakeId ? ' (bez ujęcia)' : ''}
                  </option>
                ))}
              </SearchableSelect>
            </label>
            {intakeFetchError && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 -mt-2">
                {intakeFetchError}
              </div>
            )}
            {wybraneIntake && intakeMaCoords && (
              <div className="text-[11px] text-emerald-700 -mt-2">
                ✓ Ujęcie „{wybraneIntake.name}": {Number(wybraneIntake.latitude).toFixed(5)}, {Number(wybraneIntake.longitude).toFixed(5)}
              </div>
            )}
            {wybraneIntake && !intakeMaCoords && !bbox && !intakeFetchError && (
              <div className="text-[11px] text-amber-600 -mt-2">
                Ujęcie „{wybraneIntake.name}" nie ma współrzędnych. Narysuj obszar na mapie albo uzupełnij lat/lng w karcie ujęcia.
              </div>
            )}
            {wybraneIntake && (() => {
              const boreholesWithCoords = (wybraneIntake.boreholes ?? [])
                .filter(b => b.latitude != null && b.longitude != null);
              const noBoreholeCoords = boreholesWithCoords.length === 0;
              return (
                <fieldset className="field">
                  <span className="field-label">Markery na mapie</span>
                  <div className="flex gap-2">
                    {([
                      { value: 'intake' as MarkerMode, label: 'Ujęcie', hint: 'Pojedynczy pin z nazwą ujęcia' },
                      { value: 'boreholes' as MarkerMode, label: `Otwory (${boreholesWithCoords.length})`, hint: 'Markery wszystkich studni z oznaczeniami' },
                    ]).map(opt => {
                      const disabled = opt.value === 'boreholes' && noBoreholeCoords;
                      return (
                        <label
                          key={opt.value}
                          title={disabled ? 'Brak studni z współrzędnymi' : opt.hint}
                          className={
                            'flex-1 text-center px-2 py-1.5 rounded border text-[12px] focus-within:ring-2 focus-within:ring-water-500/50 '
                            + (disabled
                                ? 'border-ink-100 text-ink-300 bg-ink-50 cursor-not-allowed'
                                : 'cursor-pointer ' + (markerMode === opt.value
                                    ? 'border-ink-700 bg-ink-50 font-semibold'
                                    : 'border-ink-200 hover:border-ink-400'))
                          }
                        >
                          <input
                            type="radio"
                            name="markerMode"
                            value={opt.value}
                            checked={markerMode === opt.value}
                            disabled={disabled}
                            onChange={() => setMarkerMode(opt.value)}
                            className="sr-only"
                          />
                          {opt.label}
                        </label>
                      );
                    })}
                  </div>
                  {markerMode === 'boreholes' && noBoreholeCoords && (
                    <div className="text-[11px] text-amber-600 mt-1">
                      Żadna studnia nie ma współrzędnych — uzupełnij lat/lng w karcie ujęcia.
                    </div>
                  )}
                </fieldset>
              );
            })()}
            <label className="field">
              <span className="field-label">Opis mapy (metryka)</span>
              <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="np. Mapa topograficzna"/>
              <span className="field-hint">Pole „Opis" w metryce w prawym dolnym rogu.</span>
            </label>
            <div className="flex gap-2">
              <label className="field flex-1">
                <span className="field-label">Kreślił</span>
                <input value={drawnBy} onChange={e => setDrawnBy(e.target.value)} className="input" placeholder="Imię i nazwisko"/>
              </label>
              <label className="field w-24">
                <span className="field-label">Rys. nr</span>
                <input value={drawingNumber} onChange={e => setDrawingNumber(e.target.value)} className="input" placeholder="1"/>
              </label>
            </div>
            <label className="field">
              <span className="field-label">Format</span>
              <SearchableSelect value={format} onChange={v => setFormat(v as 'A2' | 'A3' | 'A4')}>
                <option value="A2">A2 (594 × 420 mm)</option>
                <option value="A3">A3 (420 × 297 mm)</option>
                <option value="A4">A4 (297 × 210 mm)</option>
              </SearchableSelect>
            </label>
            <label className="field">
              <span className="field-label">Skala (gdy bbox z ujęcia)</span>
              <SearchableSelect value={skala} onChange={setSkala}>
                <option value="1:1 000">1:1 000</option>
                <option value="1:2 000">1:2 000</option>
                <option value="1:5 000">1:5 000</option>
                <option value="1:10 000">1:10 000</option>
                <option value="1:25 000">1:25 000</option>
                <option value="1:50 000">1:50 000</option>
                <option value="1:100 000">1:100 000</option>
              </SearchableSelect>
            </label>
            <fieldset className="field">
              <span className="field-label">Format pliku</span>
              <div className="flex gap-2">
                {(['PDF', 'PNG'] as const).map(f => (
                  <label key={f} className={'flex-1 text-center px-2 py-1.5 rounded border cursor-pointer text-[12px] focus-within:ring-2 focus-within:ring-water-500/50 ' + (fileType === f ? 'border-ink-700 bg-ink-50 font-semibold' : 'border-ink-200 hover:border-ink-400')}>
                    <input type="radio" name="fileType" value={f} checked={fileType === f}
                      onChange={() => setFileType(f)} className="sr-only"/>
                    {f}
                  </label>
                ))}
              </div>
            </fieldset>
            <button disabled={busy || !canFetch} onClick={onFetch} className="btn btn-primary w-full justify-center mt-2">
              {busy
                ? <><Spinner className="w-3.5 h-3.5"/> Generuję mapę…</>
                : <><Icon name="download" className="w-3.5 h-3.5"/> Pobierz {fileType} ({visibleCount})</>}
            </button>
            <div className="text-[11px] text-ink-400 mt-1">
              Mapa zapisuje się w MinIO (<span className="font-mono">mapy-cache</span>) i pobiera lokalnie. Generowanie 2–60 s zależnie od liczby warstw.
            </div>
            <div className="text-[11px] text-ink-400">
              Kolejność rysowania z podglądu jest zachowana w eksporcie; krycie w pliku wynika z ustawień serwerowych warstw.
            </div>

            {phases.length > 0 && (
              <div className="mt-3 border border-ink-100 rounded-md p-2 bg-ink-50/40 max-h-[280px] overflow-auto">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 mb-1.5">
                  Postęp ({phases.length} {phases.length === 1 ? 'krok' : phases.length < 5 ? 'kroki' : 'kroków'})
                </div>
                <ol className="flex flex-col gap-1">
                  {phases.map((p, i) => {
                    const isLast = i === phases.length - 1;
                    const isCurrent = busy && isLast;
                    const failed = p.key === 'wms-fail';
                    return (
                      <li key={i} className={'flex items-start gap-2 text-[11px] ' + (failed ? 'text-amber-600' : isCurrent ? 'text-ink-800' : 'text-ink-500')}>
                        <span className="shrink-0 mt-0.5">
                          {isCurrent ? (
                            <Spinner className="w-3 h-3 text-ai-600"/>
                          ) : failed ? (
                            <span className="inline-block w-3 h-3 rounded-full bg-amber-500 text-white text-[8px] leading-[12px] text-center font-bold">!</span>
                          ) : (
                            <Icon name="check" className="w-3 h-3 text-green-600"/>
                          )}
                        </span>
                        <span className="flex-1">
                          {p.label}
                          {p.current != null && p.total != null && (
                            <span className="ml-1 text-ink-400">({p.current}/{p.total})</span>
                          )}
                        </span>
                        {p.elapsedMs != null && (
                          <span className="shrink-0 font-mono text-[10px] text-ink-400">
                            {(p.elapsedMs / 1000).toFixed(1)}s
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>

        {/* pasek stanu mapy — obszar eksportu, współrzędne kursora, parametry pliku */}
        <div className="px-3 py-1.5 text-[11px] text-ink-500 border-t border-ink-100 flex items-center justify-between gap-3">
          <span className="truncate">
            {bbox
              ? <>Obszar: <span className="font-mono">{bbox.map(v => v.toFixed(5)).join(', ')}</span></>
              : <>Domyślny obszar liczony z lokalizacji ujęcia + skala × format. Możesz narysować własny.</>}
          </span>
          <span className="shrink-0 font-mono text-ink-600 hidden md:inline">
            {cursor ? `${cursor.lat.toFixed(5)}°N · ${cursor.lng.toFixed(5)}°E` : '— · —'} · WGS84
          </span>
          <span className="shrink-0 text-ink-400">Eksport: {format} · {skala} · {fileType}</span>
        </div>
      </div>

      {/* Admin: warstwy lokalne PostGIS — zwijana sekcja pod hero */}
      {isAdmin && localLayers && localLayers.length > 0 && (
        <div className="card mt-4">
          <button className="card-head w-full text-left" onClick={() => setLocalOpen(o => !o)}>
            <div className="card-title">Warstwy lokalne (PostGIS)</div>
            <span className="ml-2 text-[11px] text-ink-400">
              dane GDOŚ blokowane WAF — pobierane z EEA discomap i hostowane lokalnie
            </span>
            <Icon name="chevron-down" className={'w-4 h-4 ml-auto text-ink-400 transition ' + (localOpen ? '' : '-rotate-90')}/>
          </button>
          {localOpen && (
            <div className="card-body !p-0">
              <table className="table-hw">
                <thead>
                  <tr>
                    <th>Warstwa</th>
                    <th>Źródło</th>
                    <th className="text-right">Obiekty</th>
                    <th>Ostatnie pobranie</th>
                    <th>Status</th>
                    <th className="text-right">Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {localLayers.map(l => {
                    const fetchedAt = l.fetchedAt ? new Date(l.fetchedAt) : null;
                    const stale = fetchedAt && (Date.now() - fetchedAt.getTime()) > 1000 * 60 * 60 * 24 * 90;
                    const rowBusy = refreshingLocal === l.code || l.refreshInProgress;
                    return (
                      <tr key={l.code}>
                        <td>
                          <div className="font-semibold text-ink-900 text-[13px]">{l.displayName}</div>
                          <div className="font-mono text-[10px] text-ink-400">{l.code}</div>
                        </td>
                        <td className="text-[11px] text-ink-600 max-w-[380px] truncate" title={l.sourceUrl}>
                          EEA discomap
                          {l.sourceQueryFilter && (
                            <span className="font-mono text-[10px] text-ink-400 ml-1">[{l.sourceQueryFilter}]</span>
                          )}
                        </td>
                        <td className="text-right font-mono tabular-nums">
                          {l.featureCount > 0
                            ? <span className="text-ink-900 font-semibold">{l.featureCount}</span>
                            : <span className="text-ink-400">brak</span>}
                        </td>
                        <td className="text-[12px]">
                          {fetchedAt
                            ? <span className={stale ? 'text-amber-700' : 'text-ink-700'}>
                                {fetchedAt.toLocaleDateString('pl-PL')} {fetchedAt.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                                {stale && <span className="ml-1 text-[10px]">(stare)</span>}
                              </span>
                            : <span className="text-ink-400 italic">nigdy</span>}
                        </td>
                        <td className="text-[11px]">
                          {l.refreshError
                            ? <span className="text-red-600 font-mono" title={l.refreshError}>
                                błąd: {l.refreshError.length > 40 ? l.refreshError.slice(0, 40) + '…' : l.refreshError}
                              </span>
                            : rowBusy
                              ? <span className="text-water-600">pobieranie…</span>
                              : <span className="text-emerald-600">OK</span>}
                        </td>
                        <td className="text-right">
                          <button
                            onClick={() => onRefreshLocal(l.code)}
                            disabled={rowBusy}
                            className="btn btn-secondary btn-sm"
                            title={fetchedAt ? 'Pobierz aktualne dane z EEA' : 'Pierwsze pobranie danych'}
                          >
                            {rowBusy
                              ? <><svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25"/>
                                  <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                                </svg> Pobieranie…</>
                              : <>{fetchedAt ? 'Odśwież' : 'Pobierz'}</>}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
